/**
 * Horizontal collection filters carousel.
 *
 * Pages a row of filter dropdowns with a CSS transform rather than a scroller:
 * a scrolling ancestor would clip the open dropdowns.
 */
(() => {
  /* Matches `.popover::part(content)` max-width of 18.75rem in theme.css. */
  const POPOVER_MAX_WIDTH = 300;

  /*
   * `facet:update` replaces the whole section on every filter change, destroying
   * the open dropdown and the carousel. State therefore lives out here, keyed so
   * a freshly connected element can restore it. Mirrors the `openDetailsValues`
   * approach theme.js uses for the sidebar accordions.
   */
  const openPopoverIds = new Set();
  const pageBySectionId = new Map();

  /* Id of the input the user last changed, so focus can be returned to it after
     the section is replaced and the dropdown's focus trap re-activates. */
  let lastChangedInputId = null;

  class FacetsHorizontalCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.facets-horizontal__viewport');
      this.list = this.querySelector('.facets-horizontal__list');
      this.prevButton = this.querySelector('button[data-direction="prev"]');
      this.nextButton = this.querySelector('button[data-direction="next"]');

      if (!this.viewport || !this.list || !this.prevButton || !this.nextButton) {
        return;
      }

      this.sectionId = this.getAttribute('section-id') || '';
      this.page = pageBySectionId.get(this.sectionId) ?? 0;
      this.maxPage = 0;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.prevButton.addEventListener('click', () => this.goTo(this.page - 1), { signal });
      this.nextButton.addEventListener('click', () => this.goTo(this.page + 1), { signal });

      /*
       * `goTo` reads geometry in the same tick as it sets the transitioned
       * transform, so those rects are still the previous page's. Re-align once
       * the slide has settled. The synchronous call in `goTo` still covers the
       * cases where no transition runs (initial layout, reduced motion).
       */
      this.list.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName === 'transform') {
            this.alignPopovers();
          }
        },
        { signal }
      );

      /* DialogElement dispatches a bubbling `toggle` on every open/close. */
      this.addEventListener(
        'toggle',
        (event) => {
          const popover = event.target;
          if (!popover.id) return;

          if (popover.hasAttribute('open')) {
            openPopoverIds.add(popover.id);
          } else {
            openPopoverIds.delete(popover.id);
          }
        },
        { signal }
      );

      this.addEventListener(
        'change',
        (event) => {
          lastChangedInputId = event.target.id || null;
        },
        { signal }
      );

      this.addEventListener(
        'focusin',
        (event) => {
          const item = event.target.closest('.facets-horizontal__item');
          if (!item || this.maxPage === 0) return;

          const viewportWidth = this.viewport.clientWidth;
          if (viewportWidth === 0) return;

          /*
           * Measure against the track rather than the viewport: both shift by the
           * same transform, so their difference is the item's untransformed offset.
           */
          const offset = item.getBoundingClientRect().left - this.list.getBoundingClientRect().left;
          const page = Math.floor(offset / viewportWidth);

          if (page !== this.page) this.goTo(page);
        },
        { signal }
      );

      this.restoreOpenPopover();

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.list);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    /**
     * Recompute whether the row overflows and clamp the current page to the new
     * page count. Called on every resize of either the viewport or the track.
     */
    update() {
      const viewportWidth = this.viewport.clientWidth;
      const listWidth = this.list.scrollWidth;

      /* 1px tolerance absorbs sub-pixel layout rounding. */
      const overflowing = listWidth > viewportWidth + 1;
      this.toggleAttribute('overflowing', overflowing);

      this.maxPage = overflowing && viewportWidth > 0 ? Math.ceil(listWidth / viewportWidth) - 1 : 0;
      this.goTo(this.page);
    }

    goTo(page) {
      this.page = Math.max(0, Math.min(page, this.maxPage));
      pageBySectionId.set(this.sectionId, this.page);

      const viewportWidth = this.viewport.clientWidth;
      const maxOffset = Math.max(0, this.list.scrollWidth - viewportWidth);
      const offset = Math.min(this.page * viewportWidth, maxOffset);

      this.list.style.transform = `translateX(${this.isRtl ? offset : -offset}px)`;

      this.prevButton.disabled = this.page === 0;
      this.nextButton.disabled = this.page >= this.maxPage;

      this.alignPopovers();
    }

    /**
     * Reopen whichever dropdown was open before the section was replaced.
     * `open="immediate"` skips the enter animation, so the dropdown appears to
     * have stayed open rather than reopening.
     */
    restoreOpenPopover() {
      let restored = false;

      openPopoverIds.forEach((id) => {
        const popover = this.querySelector(`#${CSS.escape(id)}`);
        if (popover && !popover.hasAttribute('open')) {
          popover.setAttribute('open', 'immediate');
          restored = true;
        }
      });

      if (restored && lastChangedInputId) {
        const inputId = lastChangedInputId;
        requestAnimationFrame(() => {
          document.getElementById(inputId)?.focus();
        });
      }
    }

    /**
     * Flip a dropdown to end-alignment when start-alignment would push it past
     * the viewport's clipping edge. Without this, dropdowns on the last filter
     * of a page get cut off horizontally.
     */
    alignPopovers() {
      const viewportRect = this.viewport.getBoundingClientRect();

      this.querySelectorAll('.facets-horizontal__item').forEach((item) => {
        const popover = item.querySelector('x-popover');
        if (!popover) return;

        const itemRect = item.getBoundingClientRect();
        const overflowsEnd = this.isRtl
          ? itemRect.right - POPOVER_MAX_WIDTH < viewportRect.left
          : itemRect.left + POPOVER_MAX_WIDTH > viewportRect.right;

        popover.classList.toggle('popover--bottom-end', overflowsEnd);
        popover.classList.toggle('popover--bottom-start', !overflowsEnd);
      });
    }
  }

  if (!window.customElements.get('facets-horizontal-carousel')) {
    window.customElements.define('facets-horizontal-carousel', FacetsHorizontalCarousel);
  }
})();
